import { ActionIcon, Group, LoadingOverlay, Pagination, Table, Tooltip } from '@mantine/core';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useLocation } from 'react-router';
import { IconRefresh } from '@tabler/icons-react';
import Th from '@/components/Datasets/DataSources/th';
import { ListItem } from './ListItem';
import classes from './list.module.css';
import { useIntegrationRuleStore } from '@/pages/Datasets/Integration/Tasks/useIntegrationRuleStore';
import { IntegrationRule } from '@mygpt/app';
import { useMediaQuery } from '@mantine/hooks';
import { useDataSourcesModal } from '@mygpt/app/datasets';

const LazyIntegrationTasksPage = () => {
  const { t } = useTranslation();

  const { datasetId } = useParams();
  const location = useLocation();
  const { pathname } = location;

  // 从 URL 路径中提取 botId
  const botId = useMemo(() => {
    const match = pathname.match(/\/bot\/([^/]+)/);
    return match ? match[1] : '';
  }, [pathname]);
  const {
    sortBy,
    reverseSortDirection,
    ruleData,
    loading,
    fetchRules,
    setSorting,
    refreshStatus,
    refreshStatusLoading,
  } = useIntegrationRuleStore();
  const matches = useMediaQuery('(min-width: 640px)');
  const { counter } = useDataSourcesModal();

  const onPageChange = (page: number) => {
    fetchRules({
      datasetId,
      botId,
      page,
      size: ruleData.size,
      order_by: `${reverseSortDirection ? '-' : ''}${sortBy}`,
    });
  };

  useEffect(() => {
    (async () => {
      if (!datasetId) return;
      await fetchRules({
        datasetId,
        botId,
        page: ruleData.page,
        size: ruleData.size,
        order_by: `${reverseSortDirection ? '-' : ''}${sortBy}`,
      });
    })();
  }, [datasetId, botId, counter]);

  return (
    <section className="flex flex-col relative h-full">
      <section className={`${!loading && 'flex-auto'} relative flex flex-col`}>
        <section className="flex-auto">
          <section className="h-full rounded-xl overflow-hidden">
            <Table className={classes.table} verticalSpacing="sm" highlightOnHover captionSide="bottom">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('create_v2.integrations.task_management.name')}</Table.Th>
                  <Table.Th>{t('create_v2.integrations.task_management.share_urls')}</Table.Th>
                  <Table.Th>{t('create_v2.integrations.task_management.created_by')}</Table.Th>
                  <Th
                    sorted={sortBy === 'created_at'}
                    reversed={reverseSortDirection}
                    onSort={() => setSorting('created_at', datasetId, botId)}
                  >
                    {t('create_v2.integrations.task_management.created_at')}
                  </Th>
                  <Table.Th>{t('create_v2.integrations.task_management.file_count')}</Table.Th>
                  <Table.Th className="w-[150px]">
                    <Group gap={4}>
                      {t('embeddings.table.status')}
                      <Tooltip label={t('embeddings.table.refresh_status_tip')}>
                        <ActionIcon
                          size="xs"
                          variant="subtle"
                          color="gray"
                          loading={refreshStatusLoading}
                          onClick={() => refreshStatus(datasetId)}
                        >
                          <IconRefresh size={14} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Th>
                  <Th
                    sorted={sortBy === 'last_synced_at'}
                    reversed={reverseSortDirection}
                    onSort={() => setSorting('last_synced_at', datasetId, botId)}
                  >
                    {t('create_v2.integrations.task_management.last_synced_at')}
                  </Th>
                  <Table.Th>{t('create_v2.integrations.task_management.sync_interval')}</Table.Th>
                  <Table.Th>{t('create_v2.integrations.task_management.action')}</Table.Th>
                </Table.Tr>
              </Table.Thead>

              <Table.Tbody>
                <LoadingOverlay className={'min-h-[200px]'} visible={loading} />
                {ruleData.items.map((item: IntegrationRule) => (
                  <ListItem key={item.id} item={item} botId={botId} />
                ))}
              </Table.Tbody>
            </Table>
          </section>
        </section>
        <section className="flex-none">
          {ruleData.pages > 1 && (
            <Group className="px-2 sm:px-8 mt-5" justify="center">
              <Pagination
                value={ruleData.page}
                total={ruleData.pages}
                size={matches ? 'md' : 'sm'}
                onChange={onPageChange}
              />
            </Group>
          )}
        </section>
      </section>
    </section>
  );
};

export default LazyIntegrationTasksPage;
